import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { usePostHog } from 'posthog-js/react';
import { useAuth } from '../context/AuthContext';
import supabase from '../utils/supabaseClient';
import logger from '../utils/logger';
import {
  isPushSupported,
  getPushPermissionState,
  subscribeToPush,
  unsubscribeFromPush,
  hasDevicePushSubscription,
  isIOSSafari,
  isStandaloneMode,
} from '../services/pushService';
import { trackConversion } from '../utils/gtmHelpers';

// AsyncStorage keys
const KEY_VISIT_COUNT = 'notification_visit_count';
const KEY_FIRST_SAVE_DISMISSED = 'prompt_first_save_dismissed';
const KEY_SESSION_BANNER_DISMISSED = 'prompt_session_banner_dismissed';
const KEY_RETURN_PUSH_DISMISSED = 'prompt_return_push_dismissed';
// Lifetime show counter for the contextual listing-detail opt-in re-ask.
// Persisted as an integer string; capped at LISTING_CONTEXT_MAX_LIFETIME shows.
const KEY_LISTING_CONTEXT_SHOWN_COUNT = 'prompt_listing_context_shown_count';
const LISTING_CONTEXT_MAX_LIFETIME = 2;

/**
 * Central hook for notification prompt visibility and actions.
 *
 * Returns flags for which prompts to show + action functions.
 */
export function useNotificationPrompts() {
  const { user } = useAuth();
  const posthog = usePostHog();

  // DB-backed opt-in states
  const [emailOptedIn, setEmailOptedIn] = useState(false);
  const [pushOptedIn, setPushOptedIn] = useState(false);
  const [accountCreatedAt, setAccountCreatedAt] = useState(null);

  // Per-device states (AsyncStorage)
  const [visitCount, setVisitCount] = useState(0);
  const [firstSaveDismissed, setFirstSaveDismissed] = useState(true); // default true = hidden
  const [sessionBannerDismissed, setSessionBannerDismissed] = useState(true);
  const [returnPushDismissed, setReturnPushDismissed] = useState(true);
  // Lifetime count of contextual listing-context prompt shows (persisted).
  // Default high = hidden until the persisted value is loaded, so we never
  // flash the prompt before the cap is known.
  const [listingContextShownCount, setListingContextShownCount] = useState(
    LISTING_CONTEXT_MAX_LIFETIME
  );

  // Session state
  const [viewedListingsCount, setViewedListingsCount] = useState(0);
  // True once the contextual prompt has been shown OR dismissed this session,
  // so it can appear at most once per session.
  const [listingContextSessionDone, setListingContextSessionDone] = useState(false);
  const [pushPermission, setPushPermission] = useState(() => getPushPermissionState());

  const loadedRef = useRef(false);

  // Load persisted state on mount
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    (async () => {
      try {
        const [vc, fsd, sbd, rpd, lcsc] = await Promise.all([
          AsyncStorage.getItem(KEY_VISIT_COUNT),
          AsyncStorage.getItem(KEY_FIRST_SAVE_DISMISSED),
          AsyncStorage.getItem(KEY_SESSION_BANNER_DISMISSED),
          AsyncStorage.getItem(KEY_RETURN_PUSH_DISMISSED),
          AsyncStorage.getItem(KEY_LISTING_CONTEXT_SHOWN_COUNT),
        ]);

        const newVisitCount = (parseInt(vc, 10) || 0) + 1;
        setVisitCount(newVisitCount);
        await AsyncStorage.setItem(KEY_VISIT_COUNT, String(newVisitCount));

        setFirstSaveDismissed(fsd === 'true');
        setSessionBannerDismissed(sbd === 'true');
        setReturnPushDismissed(rpd === 'true');
        setListingContextShownCount(parseInt(lcsc, 10) || 0);
      } catch (err) {
        logger.error('[useNotificationPrompts] Failed to load state:', err);
      }
    })();
  }, []);

  // Load user opt-in state from DB + device push state
  useEffect(() => {
    if (!user?.id) return;

    (async () => {
      try {
        const [{ data, error }, deviceHasPush] = await Promise.all([
          supabase
            .from('users')
            .select('email_digest_opted_in, created_at')
            .eq('id', user.id)
            .single(),
          hasDevicePushSubscription(),
        ]);

        if (error) throw error;

        setEmailOptedIn(!!data.email_digest_opted_in);
        setPushOptedIn(deviceHasPush);
        setAccountCreatedAt(data.created_at ? new Date(data.created_at) : null);
      } catch (err) {
        logger.error('[useNotificationPrompts] Failed to load user state:', err);
      }
    })();
  }, [user?.id]);

  // Dismiss a prompt permanently
  const dismissPrompt = useCallback(async (promptKey) => {
    const keyMap = {
      first_save: KEY_FIRST_SAVE_DISMISSED,
      session_banner: KEY_SESSION_BANNER_DISMISSED,
      return_push: KEY_RETURN_PUSH_DISMISSED,
    };
    const storageKey = keyMap[promptKey];
    if (!storageKey) return;

    try {
      await AsyncStorage.setItem(storageKey, 'true');
    } catch (err) {
      logger.error('[useNotificationPrompts] Failed to dismiss:', err);
    }

    if (promptKey === 'first_save') setFirstSaveDismissed(true);
    if (promptKey === 'session_banner') setSessionBannerDismissed(true);
    if (promptKey === 'return_push') setReturnPushDismissed(true);
  }, []);

  // Enable email digest
  const enableEmailDigest = useCallback(async () => {
    if (!user?.id) return false;

    try {
      const { error } = await supabase
        .from('users')
        .update({ email_digest_opted_in: true })
        .eq('id', user.id);

      if (error) throw error;

      setEmailOptedIn(true);

      trackConversion(
        'email_alerts_subscribed',
        { user_id: user.id, source: 'prompt' },
        { email: user.email }
      );

      return true;
    } catch (err) {
      logger.error('[useNotificationPrompts] Failed to enable email digest:', err);
      return false;
    }
  }, [user?.id, user?.email]);

  // Enable push notifications (triggers browser prompt)
  const enablePushNotifications = useCallback(async () => {
    if (!isPushSupported()) {
      return { success: false, permissionState: 'unsupported' };
    }

    const result = await subscribeToPush();

    if (result.success) {
      setPushOptedIn(true);
      setPushPermission('granted');
    } else {
      setPushPermission(result.permissionState);
    }

    return result;
  }, []);

  // Disable push notifications
  const disablePushNotifications = useCallback(async () => {
    const success = await unsubscribeFromPush();
    if (success) {
      setPushOptedIn(false);
    }
    return success;
  }, []);

  // Increment viewed listings count (call from feed detail navigation)
  const trackListingView = useCallback(() => {
    setViewedListingsCount((c) => c + 1);
  }, []);

  // Mark the contextual listing-context prompt as shown for this session and
  // bump the persisted lifetime counter. Idempotent within a session via
  // listingContextSessionDone. Mirrors how dismissPrompt persists guards.
  const markListingContextShown = useCallback(async () => {
    setListingContextSessionDone(true);
    setListingContextShownCount((c) => {
      const next = c + 1;
      AsyncStorage.setItem(KEY_LISTING_CONTEXT_SHOWN_COUNT, String(next)).catch(
        (err) =>
          logger.error(
            '[useNotificationPrompts] Failed to persist listing-context count:',
            err
          )
      );
      return next;
    });
    posthog?.capture('email_prompt_listing_context_shown');
  }, [posthog]);

  // Quiet dismiss of the contextual prompt (no permanent flag: the lifetime
  // counter already caps total shows; this just closes it for the session).
  const dismissListingContextPrompt = useCallback(() => {
    setListingContextSessionDone(true);
    posthog?.capture('email_prompt_listing_context_dismissed');
  }, [posthog]);

  // Enable email digest from the contextual prompt. Wraps the shared
  // enableEmailDigest so the contextual variant gets its own success event.
  const enableEmailDigestFromListingContext = useCallback(async () => {
    const success = await enableEmailDigest();
    if (success) {
      setListingContextSessionDone(true);
      posthog?.capture('email_prompt_listing_context_enabled');
    }
    return success;
  }, [enableEmailDigest, posthog]);

  // Compute prompt visibility
  const isAccountUnder24h = useMemo(() => {
    if (!accountCreatedAt) return false;
    return Date.now() - accountCreatedAt.getTime() < 24 * 60 * 60 * 1000;
  }, [accountCreatedAt]);

  const showFirstSaveToast = !emailOptedIn && !firstSaveDismissed;

  // Contextual opt-in re-ask: fires at a genuine value moment, the first
  // listing detail viewed this session, for users not yet opted in.
  // Caps: once per session (listingContextSessionDone) and at most
  // LISTING_CONTEXT_MAX_LIFETIME shows ever (persisted counter).
  const showListingContextPrompt =
    !emailOptedIn &&
    viewedListingsCount >= 1 &&
    !listingContextSessionDone &&
    listingContextShownCount < LISTING_CONTEXT_MAX_LIFETIME;

  const showSessionBanner =
    !emailOptedIn &&
    viewedListingsCount >= 5 &&
    isAccountUnder24h &&
    !sessionBannerDismissed;

  // Show on iOS Safari (non-standalone) for A2HS instructions,
  // or on web/Android when push is supported and not denied
  const isIOSNeedsInstall = isIOSSafari() && !isStandaloneMode();
  const showReturnPushCard =
    visitCount >= 2 &&
    !pushOptedIn &&
    (isPushSupported() || isIOSNeedsInstall) &&
    pushPermission !== 'denied' &&
    !returnPushDismissed;

  // Unified notification card: show when email OR push can still be enabled
  const pushCanBeEnabled = (isPushSupported() || isIOSNeedsInstall) && pushPermission !== 'denied';
  const showNotificationCard =
    !emailOptedIn || (!pushOptedIn && pushCanBeEnabled);

  return {
    // Opt-in states
    emailOptedIn,
    pushOptedIn,
    pushPermission,

    // Platform detection
    isIOSNeedsInstall,
    pushSupported: isPushSupported(),
    pushPermissionDenied: pushPermission === 'denied',

    // Prompt visibility
    showFirstSaveToast,
    showSessionBanner,
    showReturnPushCard,
    showNotificationCard,
    showListingContextPrompt,

    // Session tracking
    viewedListingsCount,
    trackListingView,
    visitCount,

    // Actions
    dismissPrompt,
    enableEmailDigest,
    enablePushNotifications,
    disablePushNotifications,
    // Contextual listing-detail re-ask actions
    markListingContextShown,
    dismissListingContextPrompt,
    enableEmailDigestFromListingContext,
  };
}
